/**
 * ローカルキャッシュ（IndexedDB / Dexie）。SPEC §2.1
 *
 * D1 が単一の真実で、ここは「描画元のキャッシュ」＋「未送信の書き込みキュー」。
 * UI は必ずこの DB を読む（API のレスポンスを直接描画しない）ので、
 * 楽観的更新もオフライン閲覧も同じ経路になる。
 */
import Dexie, { type Table } from 'dexie';
import type { ItemRow, LotRow, PurchaseRow } from '../../shared/types';

export type QueueStatus = 'pending' | 'failed';

/** 未送信の書き込みリクエスト 1 件 */
export interface QueueOp {
  /** 自動採番。送信順序＝この昇順（依存関係を壊さないため必ず順番に送る） */
  seq?: number;
  method: 'POST' | 'PATCH' | 'DELETE';
  path: string;
  body?: unknown;
  /** 未送信一覧に出す説明（例: 「シャンプー を開封」） */
  label: string;
  attempts: number;
  /** epoch ms。指数バックオフの次回送信時刻 */
  nextAttemptAt: number;
  /** pending = 再送対象 / failed = 上限超過 or 恒久エラー（手動再送待ち） */
  status: QueueStatus;
  lastError?: string;
  createdAt: number;
}

export interface MetaRow {
  key: string;
  value: unknown;
}

class BurnLogDb extends Dexie {
  items!: Table<ItemRow, string>;
  purchases!: Table<PurchaseRow, string>;
  lots!: Table<LotRow, string>;
  syncQueue!: Table<QueueOp, number>;
  meta!: Table<MetaRow, string>;

  constructor() {
    super('burn-log');
    this.version(1).stores({
      items: 'id, sort_order, name',
      purchases: 'id, item_id, purchased_at',
      lots: 'id, item_id, purchase_id',
      syncQueue: '++seq, status',
      meta: 'key',
    });
  }
}

export const db = new BurnLogDb();

/** キャッシュとキューを全消去（トークン再入力時など） */
export async function wipeLocal(): Promise<void> {
  await db.transaction('rw', db.items, db.purchases, db.lots, db.syncQueue, db.meta, async () => {
    await Promise.all([
      db.items.clear(),
      db.purchases.clear(),
      db.lots.clear(),
      db.syncQueue.clear(),
      db.meta.clear(),
    ]);
  });
}
